import { toast } from '@/lib/toast'

/** The text of a caught error, or `fallback` when it carries none. */
export function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof Error && caught.message.trim() !== '') return caught.message
  if (typeof caught === 'string' && caught.trim() !== '') return caught
  return fallback
}

/**
 * The one error toast (U-048, IA-21/IA-26): what failed, in our words (`what`, a short
 * sentence with a full stop: "Taak opslaan mislukt."), and the server's reason underneath
 * when it adds something. With `retry` the toast offers "Opnieuw proberen".
 */
export function toastFailure(caught: unknown, what: string, retry?: () => void): void {
  const reason = errorText(caught, what)
  toast.error(what, {
    ...(reason !== what ? { description: reason } : {}),
    ...(retry ? { action: { label: 'Opnieuw proberen', onClick: retry } } : {}),
  })
}

export interface FeedbackOptions {
  /** The toast when it fails, e.g. "Taak opslaan mislukt." */
  error: string
  /** A success toast, only where the result is not visible on the screen itself. */
  success?: string
  /** Offer "Opnieuw proberen" on the error toast, which runs the same action again. */
  retry?: boolean
}

/**
 * Runs `action` and reports a failure as a toast instead of swallowing it. Resolves to
 * the result, or to `undefined` after a failure: it never rejects, so `void
 * runWithFeedback(…)` leaves no unhandled rejection (which would raise the global error
 * overlay of `errorReporter`).
 */
export async function runWithFeedback<T>(
  action: () => Promise<T>,
  options: FeedbackOptions,
): Promise<T | undefined> {
  try {
    const result = await action()
    if (options.success) toast.success(options.success)
    return result
  } catch (caught) {
    toastFailure(
      caught,
      options.error,
      options.retry ? () => void runWithFeedback(action, options) : undefined,
    )
    return undefined
  }
}

/**
 * An optimistic change (IA-26): `apply` shows it at once, `request` saves it, and when
 * saving fails `revert` puts the screen back and a toast says what went wrong, with
 * "Opnieuw proberen". Resolves to whether it was saved; never rejects.
 */
export async function optimistic(
  apply: () => void,
  revert: () => void,
  request: () => Promise<unknown>,
  error: string,
): Promise<boolean> {
  apply()
  try {
    await request()
    return true
  } catch (caught) {
    revert()
    toastFailure(caught, error, () => void optimistic(apply, revert, request, error))
    return false
  }
}
